import { createClient } from '@supabase/supabase-js';
import type { Database } from '../types/database';
import { authLogger } from './auth/authLogger';

// Validate environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseKey) {
  throw new Error('Missing Supabase environment variables');
}

// Create Supabase client with auth configuration
export const supabase = createClient<Database>(supabaseUrl, supabaseKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: false, // Disable URL detection to prevent unwanted redirects
    storage: {
      // Persist session data with proper error handling
      getItem: (key) => {
        const value = localStorage.getItem(key);
        authLogger.debug('STORAGE', 'Get item', { 
          key, 
          exists: !!value,
          timestamp: new Date().toISOString()
        });
        // Validate stored session data
        if (key.includes('auth.token') && value) {
          try {
            const parsed = JSON.parse(value);
            // Only validate token existence, not expiration
            if (!parsed.access_token) {
              authLogger.debug('STORAGE', 'Invalid session data, removing', { key });
              localStorage.removeItem(key);
              return null;
            }
          } catch (error) {
            authLogger.error('ERROR', 'Failed to parse session data', error);
            localStorage.removeItem(key);
            return null;
          }
        }
        return value;
      },
      setItem: (key, value) => {
        authLogger.debug('STORAGE', 'Set item', { 
          key,
          timestamp: new Date().toISOString()
        });
        // Ensure we're not storing invalid session data
        if (key.includes('auth.token') && value) {
          try {
            const parsed = JSON.parse(value);
            if (!parsed.access_token || !parsed.expires_at) {
              authLogger.debug('STORAGE', 'Preventing invalid session storage', { key });
              return;
            }
          } catch (error) {
            authLogger.error('ERROR', 'Failed to validate session data', error);
            return;
          }
        }
        localStorage.setItem(key, value);
      },
      removeItem: (key) => {
        authLogger.debug('STORAGE', 'Remove item', { 
          key,
          timestamp: new Date().toISOString()
        });
        localStorage.removeItem(key);
        // Clear all related auth items to prevent stale state
        if (key.includes('auth')) {
          localStorage.removeItem('sb-access-token');
          localStorage.removeItem('sb-refresh-token');
          localStorage.removeItem('supabase.auth.token');
          localStorage.removeItem('sb-auth-token');
          localStorage.removeItem('auth_token_expires_at');
          localStorage.removeItem('auth_token');
          localStorage.removeItem('dga_session_state');
        }
      }
    },
    detectSessionInUrl: true,
    flowType: 'pkce',
    debug: true // Enable debug logging for auth issues
  },
  global: {
    headers: {
      'X-Client-Info': 'decision-guide-ai',
      'X-Client-Version': '1.0.0'
    }
  }
});

// Profile management with proper session handling
export async function getProfile(userId: string) {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    // Get current session
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      throw new Error('No active session');
    }

    // Fetch profile with RLS
    const { data: profile, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle();

    if (error) {
      authLogger.error('ERROR', 'Profile fetch failed', {
        error,
        userId,
        sessionId: session.user.id
      });
      throw error;
    }

    return { data: profile, error: null };
  } catch (error) {
    authLogger.error('ERROR', 'Failed to fetch profile', error);
    return { 
      data: null, 
      error: error instanceof Error ? error : new Error('Failed to fetch profile')
    };
  }
}

// Update profile with proper error handling
export async function updateProfile(userId: string, updates: Partial<Database['public']['Tables']['user_profiles']['Update']>) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      throw new Error('No active session');
    }

    const { data, error } = await supabase
      .from('user_profiles')
      .update(updates)
      .eq('id', userId)
      .select()
      .single();

    if (error) {
      throw error;
    }

    return { data, error: null };
  } catch (error) {
    authLogger.error('ERROR', 'Profile update failed', error);
    return { 
      data: null, 
      error: error instanceof Error ? error : new Error('Failed to update profile')
    };
  }
}

// Decision management functions
export async function getDecisions(userId: string) {
  try {
    if (!userId) {
      return { data: null, error: new Error('User ID is required') };
    }

    // Get current session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError) {
      return { data: null, error: new Error('Failed to validate session') };
    }

    if (!session) {
      return { data: null, error: new Error('No active session - please sign in') };
    }

    // Fetch decisions with proper error handling
    const { data, error } = await supabase
      .from('decisions')
      .select(`
        id, title, type, reversibility, importance, created_at, updated_at, user_id,
        description:description
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      authLogger.error('ERROR', 'Failed to fetch decisions', error);
      if (error.code === 'PGRST301' || error.message.includes('permission')) {
        return { data: null, error: new Error('You do not have permission to access these decisions') };
      } else if (error.code === '42P01') {
        return { data: null, error: new Error('Database setup incomplete. Please try again later.') };
      }
      return { data: null, error };
    }

    return { data, error: null };
  } catch (error) {
    authLogger.error('ERROR', 'Decisions fetch error', error);
    
    let errorMessage = 'An unexpected error occurred while fetching decisions';
    if (error instanceof Error) {
      if (error.message.includes('JWT')) {
        errorMessage = 'Your session has expired. Please sign in again.';
      } else if (error.message.includes('network')) {
        errorMessage = 'Unable to connect to the server. Please check your internet connection.';
      } else {
        errorMessage = error.message;
      }
    }
    return { data: null, error: new Error(errorMessage) };
  }
}

export async function createDecision(decision: Omit<Database['public']['Tables']['decisions']['Insert'], 'id' | 'created_at' | 'updated_at'>) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      throw new Error('No active session');
    }

    const { data, error } = await supabase
      .from('decisions')
      .insert([decision])
      .select()
      .single();

    if (error) {
      throw error;
    }

    return { data, error: null };
  } catch (error) {
    authLogger.error('ERROR', 'Decision creation error', error);
    return { data: null, error };
  }
}

export async function deleteDecision(id: string) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      throw new Error('No active session');
    }

    const { error } = await supabase
      .from('decisions')
      .delete()
      .eq('id', id);

    if (error) {
      throw error;
    }

    return { error: null };
  } catch (error) {
    authLogger.error('ERROR', 'Decision deletion error', error);
    return { error };
  }
}

// Auth state helpers
export async function refreshSession() {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error) {
      throw error;
    }

    if (!session) {
      const { error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError) {
        throw refreshError;
      }
    }

    return { session, error: null };
  } catch (error) {
    authLogger.error('ERROR', 'Session refresh failed', error);
    return { session: null, error };
  }
}

// Export auth methods directly
export const { auth } = supabase;